import { defineStore } from "pinia";
import { createBaseStoreRefactor } from "./baseStoreRefactor";

/**
 * Dashboard Coordination Store - lightweight coordination without data duplication
 * This store coordinates loading of dashboard data across main stores
 * without duplicating data, maintaining single source of truth
 */

const dashboardCoordinationBaseStore = createBaseStoreRefactor({
  endpoint: "/api/v1/day_reports",
  name: "DashboardCoordinationStore",
  objectName: "Dashboard",
  enableCache: true, // Cache dashboard coordination state
  infiniteLoading: false,
});

export const useDashboardCoordinationStore = defineStore(
  "dashboardCoordination",
  {
    ...dashboardCoordinationBaseStore,

    state: () => ({
      ...dashboardCoordinationBaseStore.state(),

      // Dashboard coordination state
      currentDayReportId: null,
      loadedAccountIds: [], // Track which accounts we've ensured are loaded
      loadedReportIds: [], // Track which reports we've ensured are loaded
      dashboardReady: false,

      // Performance tracking
      loadStartTime: null,
      loadEndTime: null,

      // Coordination metadata
      coordinationHistory: [], // Track recent coordinations for debugging
    }),

    getters: {
      ...dashboardCoordinationBaseStore.getters,

      // Dashboard state getters
      isDashboardReady: (state) => state.dashboardReady,
      getCurrentDayReportId: (state) => state.currentDayReportId,
      getLoadedAccountIds: (state) => state.loadedAccountIds,
      getLoadedReportIds: (state) => state.loadedReportIds,

      // Performance getters
      getLoadDuration: (state) => {
        if (state.loadStartTime && state.loadEndTime) {
          return state.loadEndTime - state.loadStartTime;
        }
        return null;
      },

      // Coordination status
      getCoordinationStatus: (state) => ({
        ready: state.dashboardReady,
        dayReportId: state.currentDayReportId,
        accountCount: state.loadedAccountIds.length,
        reportCount: state.loadedReportIds.length,
        loading: state.loading,
        error: state.error,
        loadDuration:
          state.loadEndTime && state.loadStartTime
            ? state.loadEndTime - state.loadStartTime
            : null,
      }),

      // Expose configuration for tests
      endpoint: () => "/api/v1/day_reports",
    },

    actions: {
      ...dashboardCoordinationBaseStore.actions,

      /**
       * Coordinate loading of dashboard data across main stores with enhanced error handling
       * This doesn't duplicate data, just ensures it's loaded in the right stores
       * @param {Number} dayReportId - Day report ID
       * @param {Object} accountsStore - Accounts store instance
       * @param {Object} reportsStore - Reports store instance
       * @param {Object} dayReportsStore - Day reports store instance
       * @param {Object} options - Loading options
       */
      async coordinateDashboardLoad(
        dayReportId,
        accountsStore,
        reportsStore,
        dayReportsStore,
        options = {},
      ) {
        if (!dayReportId) {
          throw new Error("Day report ID is required");
        }

        if (!accountsStore || !reportsStore || !dayReportsStore) {
          throw new Error("All store instances are required");
        }

        const { forceRefresh = false, silent = false } = options;

        // Check if we already coordinated for this day report (unless forcing refresh)
        if (
          !forceRefresh &&
          this.currentDayReportId === dayReportId &&
          this.dashboardReady
        ) {
          if (!silent) {
            console.log(
              `DashboardCoordination: Already coordinated for day_report ${dayReportId}, skipping`,
            );
          }
          return {
            dayReportId,
            reportCount: this.loadedReportIds.length,
            accountCount: this.loadedAccountIds.length,
            loadDuration: this.getLoadDuration,
            cached: true,
          };
        }

        this.loadStartTime = Date.now();
        this.loading = true;
        this.error = null;
        this.currentDayReportId = dayReportId;
        this.dashboardReady = false;

        try {
          if (!silent) {
            console.log(
              `DashboardCoordination: Loading data for day_report ${dayReportId}${forceRefresh ? " (force refresh)" : ""}`,
            );
          }

          // 1. Ensure day_report is loaded in dayReportsStore
          let dayReport = dayReportsStore.getDayReportById(dayReportId);
          if (!dayReport) {
            if (!silent) {
              console.log(
                `DashboardCoordination: Loading day_report ${dayReportId}`,
              );
            }
            try {
              dayReport = await dayReportsStore.getDayReport(dayReportId);
            } catch (error) {
              console.error(
                `DashboardCoordination: Failed to load day_report ${dayReportId}:`,
                error,
              );
              throw new Error(`Dagrapport ${dayReportId} niet gevonden`);
            }
          }

          // 2. Load reports for this day_report into reportsStore
          if (!silent) {
            console.log(
              `DashboardCoordination: Loading reports for day_report ${dayReportId}`,
            );
          }

          try {
            await reportsStore.getByDayReport(dayReportId, {
              forceRefresh,
              silent,
            });
          } catch (error) {
            console.warn(
              `DashboardCoordination: Failed to load reports for day_report ${dayReportId}:`,
              error,
            );
            // Continue - this is not a fatal error, we'll use what's in the store
          }

          // 3. Get account IDs from loaded reports
          const loadedReports = reportsStore.getReportsByDayReport(dayReportId);
          const accountIds = [
            ...new Set(
              loadedReports.map((report) => report.account_id).filter(Boolean),
            ),
          ];

          if (!silent) {
            console.log(
              `DashboardCoordination: Found ${loadedReports.length} reports with ${accountIds.length} unique accounts`,
            );
          }

          // 4. Ensure all needed accounts are loaded in accountsStore (with batch loading)
          const missingAccountIds = accountIds.filter(
            (accountId) => !accountsStore.getById(accountId),
          );

          if (missingAccountIds.length > 0) {
            if (!silent) {
              console.log(
                `DashboardCoordination: Loading ${missingAccountIds.length} missing accounts`,
              );
            }

            // Load accounts in batches to avoid overwhelming the server
            const ACCOUNT_BATCH_SIZE = 5;
            const accountBatches = [];

            for (
              let i = 0;
              i < missingAccountIds.length;
              i += ACCOUNT_BATCH_SIZE
            ) {
              accountBatches.push(
                missingAccountIds.slice(i, i + ACCOUNT_BATCH_SIZE),
              );
            }

            for (const batch of accountBatches) {
              const accountPromises = batch.map((accountId) =>
                accountsStore.fetchById(accountId).catch((err) => {
                  console.warn(`Failed to load account ${accountId}:`, err);
                  return null;
                }),
              );

              await Promise.all(accountPromises);
            }
          }

          // 5. Verify data integrity
          const finalReports = reportsStore.getReportsByDayReport(dayReportId);
          const finalAccountIds = [
            ...new Set(
              finalReports.map((report) => report.account_id).filter(Boolean),
            ),
          ];

          const loadedAccounts = finalAccountIds.filter((accountId) =>
            accountsStore.getById(accountId),
          );

          // Track what we've loaded
          this.loadedAccountIds = finalAccountIds;
          this.loadedReportIds = finalReports.map((r) => r.id);
          this.dashboardReady = true;
          this.loadEndTime = Date.now();

          // Add to coordination history
          this.coordinationHistory.unshift({
            dayReportId,
            timestamp: new Date().toISOString(),
            reportCount: finalReports.length,
            accountCount: finalAccountIds.length,
            loadedAccountCount: loadedAccounts.length,
            loadDuration: this.loadEndTime - this.loadStartTime,
            success: true,
            forceRefresh,
          });

          // Keep only last 10 coordinations
          if (this.coordinationHistory.length > 10) {
            this.coordinationHistory = this.coordinationHistory.slice(0, 10);
          }

          if (!silent) {
            console.log(
              `DashboardCoordination: Dashboard ready with ${finalReports.length} reports and ${loadedAccounts.length}/${finalAccountIds.length} accounts (${this.loadEndTime - this.loadStartTime}ms)`,
            );
          }

          return {
            dayReportId,
            reportCount: finalReports.length,
            accountCount: finalAccountIds.length,
            loadedAccountCount: loadedAccounts.length,
            loadDuration: this.loadEndTime - this.loadStartTime,
            forceRefresh,
          };
        } catch (error) {
          console.error(
            "DashboardCoordination: Error loading dashboard:",
            error,
          );
          this.error =
            error.message ||
            "Er is een fout opgetreden bij het laden van het dashboard";
          this.dashboardReady = false;
          this.loadEndTime = Date.now();

          // Add failed coordination to history
          this.coordinationHistory.unshift({
            dayReportId,
            timestamp: new Date().toISOString(),
            error: error.message,
            loadDuration: this.loadEndTime - this.loadStartTime,
            success: false,
            forceRefresh,
          });

          throw error;
        } finally {
          this.loading = false;
        }
      },

      /**
       * Clear dashboard coordination state
       */
      clearDashboardState() {
        this.currentDayReportId = null;
        this.loadedAccountIds = [];
        this.loadedReportIds = [];
        this.dashboardReady = false;
        this.error = null;
        this.loadStartTime = null;
        this.loadEndTime = null;

        console.log("DashboardCoordination: Cleared dashboard state");
      },

      /**
       * Check if dashboard needs refresh
       * @param {Number} dayReportId - Day report ID to check
       */
      needsRefresh(dayReportId) {
        return this.currentDayReportId !== dayReportId || !this.dashboardReady;
      },

      /**
       * Force refresh dashboard coordination
       * @param {Number} dayReportId - Day report ID
       * @param {Object} accountsStore - Accounts store instance
       * @param {Object} reportsStore - Reports store instance
       * @param {Object} dayReportsStore - Day reports store instance
       */
      async refreshDashboard(
        dayReportId,
        accountsStore,
        reportsStore,
        dayReportsStore,
      ) {
        // Clear current state
        this.clearDashboardState();

        // Force refresh of related stores
        if (reportsStore.clear) reportsStore.clear();

        // Reload dashboard
        return this.coordinateDashboardLoad(
          dayReportId,
          accountsStore,
          reportsStore,
          dayReportsStore,
        );
      },

      /**
       * Get coordination history for debugging
       */
      getCoordinationHistory() {
        return this.coordinationHistory;
      },

      /**
       * Validate that all expected data is loaded
       * @param {Object} accountsStore - Accounts store instance
       * @param {Object} reportsStore - Reports store instance
       */
      validateDashboardData(accountsStore, reportsStore) {
        if (!this.dashboardReady) {
          return { valid: false, reason: "Dashboard not ready" };
        }

        // Check if all expected accounts are loaded
        const missingAccounts = this.loadedAccountIds.filter(
          (accountId) => !accountsStore.getAccountById(accountId),
        );

        if (missingAccounts.length > 0) {
          return {
            valid: false,
            reason: `Missing accounts: ${missingAccounts.join(", ")}`,
          };
        }

        // Check if all expected reports are loaded
        const missingReports = this.loadedReportIds.filter(
          (reportId) => !reportsStore.getById(reportId),
        );

        if (missingReports.length > 0) {
          return {
            valid: false,
            reason: `Missing reports: ${missingReports.join(", ")}`,
          };
        }

        return { valid: true };
      },
    },
  },
);
